import { isValid, parseISO } from "date-fns";
import React, { useCallback } from "react";

import { FormField, PanelFilters } from "@performyard/py-components";
import { FormCheckboxGroup, FormDateRangePicker, FormRadioButtons, FormSingleSelect } from "@performyard/py-components";

import { SingleSelectEmployee } from "../../../../components/Inputs/SelectEmployee";
import Spacer from "../../../../components/Spacer";

import type { ActionItemsFilters } from "../schemas";
import { ActionItemsFiltersSchema } from "../schemas";

import { useCurrentUser, useMeetingWorkspacesSearch } from "../../../../api/hooks";

import { fetchEmployees } from "../../../../api/services/defs/employees";

import { capitalizeWord } from "../../../../utils/stringUtils";

import { actionItemsTableConfig } from "../config";

type EmployeeValue = { id: string; name: string } | null;

export const ActionItemsFilterPanel = ({
	isOpen,
	onClose,
	onApplyFilters,
	filters,
}: {
	isOpen: boolean;
	onClose: () => void;
	onApplyFilters: (filters: ActionItemsFilters) => void;
	filters: ActionItemsFilters;
}) => {
	// Fetch employees for the employee pickers
	const handleFetchEmployees = useCallback(async (searchTerm: string) => {
		const response = await fetchEmployees({
			searchTerm,
			excludeCurrentUser: false,
			exclusionList: [],
		});
		return response.users.map((user) => ({
			id: user._id,
			name: user.name,
		}));
	}, []);

	const statusRadioOptions = actionItemsTableConfig.statusOptions.map((status) => ({
		value: status,
		label: capitalizeWord(status),
	}));

	// Get workspaces for the dropdown (user's workspaces)
	const { data: currentUser } = useCurrentUser();
	const userId = currentUser?.me?.authorization?.payload.user_id || "";
	const { data: workspaces } = useMeetingWorkspacesSearch({
		participantIds: userId ? [userId] : [],
		statusEq: "active",
		limit: 100,
	});

	const workspaceOptions =
		workspaces?.map((workspace) => ({
			value: workspace._id,
			label: workspace.name,
		})) || [];

	const normalizeDate = (date: Date | string | undefined): Date | undefined => {
		if (!date) return undefined;
		const dateObj = date instanceof Date ? date : parseISO(date);
		return isValid(dateObj) ? dateObj : undefined;
	};

	const normalizedDueDateRange = filters.dueDateRange
		? {
				from: normalizeDate(filters.dueDateRange.from),
				to: normalizeDate(filters.dueDateRange.to),
			}
		: undefined;

	const validDueDateRange =
		normalizedDueDateRange && (normalizedDueDateRange.from || normalizedDueDateRange.to)
			? normalizedDueDateRange
			: undefined;

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={ActionItemsFiltersSchema}
			defaultValues={{
				...filters,
				isUnassigned: filters.isUnassigned ?? [],
				dueDateRange: validDueDateRange,
			}}
			onSubmit={({ value }) => {
				onApplyFilters(value);
				onClose();
			}}
		>
			<FormField<EmployeeValue>
				id="createdBy"
				label="Created By"
				input={(field) => (
					<SingleSelectEmployee
						value={field.value ?? undefined}
						onChange={(employeeId, employeeName) => {
							if (employeeId && employeeName) {
								field.onChange({ id: employeeId, name: employeeName });
							} else {
								field.onChange(null);
							}
						}}
						fetchEmployees={handleFetchEmployees}
						placeholder="Search for an employee..."
						isClearable
					/>
				)}
			/>
			<Spacer height={2} />
			<FormField<EmployeeValue>
				id="assignee"
				label="Assignee"
				input={(field) => (
					<SingleSelectEmployee
						value={field.value ?? undefined}
						onChange={(employeeId, employeeName) => {
							if (employeeId && employeeName) {
								field.onChange({ id: employeeId, name: employeeName });
							} else {
								field.onChange(null);
							}
						}}
						fetchEmployees={handleFetchEmployees}
						placeholder="Search for an employee..."
						isClearable
					/>
				)}
			/>
			<Spacer height={2} />
			<FormCheckboxGroup
				id="isUnassigned"
				label="Unassigned Items"
				options={[{ value: "unassigned", label: "Show only unassigned items" }]}
			/>
			<Spacer height={2} />
			<FormRadioButtons id="status" label="Status" options={statusRadioOptions} />
			<Spacer height={2} />
			<FormDateRangePicker id="dueDateRange" label="Due Date" hasClearButton />
			<Spacer height={2} />
			<FormSingleSelect
				id="workspace"
				label="Meeting Workspace"
				options={workspaceOptions}
				placeholder="Select workspace..."
				isClearable
			/>
		</PanelFilters>
	);
};

export default ActionItemsFilterPanel;
